<script>
import { mapActions, mapGetters, mapState } from "vuex";
import GeneralMixin from "JS/mixins/campaign/general";
import FormValidationMixin from "JS/mixins/campaign/formValidation";

import {
  debounce as _debounce,
  filter as _filter,
  forEach as _forEach,
  forOwn as _forOwn,
  isEmpty as _isEmpty,
  keys as _keys,
  pickBy as _pickBy
} from "lodash";
import VueValidatorHelper from "JS/helpers/vueValidator";
import AutocompleteFieldsOption from "JS/helpers/addressAutocomplete/fieldsOption";
// conversational
import * as TweenMax from "gsap/umd/TweenMax";
import CampaignTypes from "Constants/campaign/campaignTypes";
import FacebookPrefill from "./FacebookPrefill.vue";
import CardPrimary from "../conversational/card/CardPrimary.vue";
import FieldText from "../conversational/field/FieldText.vue";
import FieldSelect from "../conversational/field/FieldSelect.vue";
import FieldCheckbox from "../conversational/field/FieldCheckbox.vue";
import FieldRadio from "../conversational/field/FieldRadio.vue";
import SegmentedProgressBar from "../UIComponents/SegmentedProgressBar.vue";

export default {
  name: "FormFields",
  components: {
    CardPrimary,
    FacebookPrefill,
    FieldText,
    FieldSelect,
    FieldCheckbox,
    FieldRadio,
    SegmentedProgressBar
  },
  mixins: [GeneralMixin, FormValidationMixin],
  props: {
    advancedAccessibility: {
      type: Boolean,
      default: false
    },
    advocateInfo: {
      required: true,
      type: Object
    },
    buttonColor: {
      type: String,
      default: "rgb(34,158,61)"
    },
    cardDescriptions: {
      type: Object,
      required: true
    },
    countryDefault: {
      required: false,
      type: String,
      default: "US"
    },
    facebookAppId: {
      type: String,
      default: ""
    },
    formHeading: {
      required: true,
      type: Object
    },
    formFields: {
      required: true,
      type: Object
    },
    formStyles: {
      type: Object,
      default: () => {}
    },
    isMultistep: {
      type: Boolean,
      required: true
    },
    language: {
      type: String,
      default: "EN"
    },
    logoImageSource: {
      type: String,
      default: ""
    },
    customFieldsValues: {
      required: true,
      type: Object
    },
    // conversational
    greetingMorning: {
      type: String,
      default: "Good morning!"
    },
    greetingAfternoon: {
      type: String,
      default: "Good afternoon!"
    },
    greetingEvening: {
      type: String,
      default: "Good evening!"
    },
    nameRequestLabel: {
      type: String,
      default: "Before we get started, <strong>what is your name</strong>?"
    },
    emailNote: {
      type: String,
    },
    phoneNote: {
      type: String,
    },
    emailQuestion: {
      type: String,
    },
    isMobile: {
      type: Boolean,
      default: false
    },
    takeActionLabel: {
      type: String,
      default: "Take Action"
    },
    nextStepLabel: {
      type: String,
      default: "Next Step"
    },
    previewMode: {
      required: true,
      type: Boolean
    },
    phoneGreeting: {
      type: String,
      default: ""
    },
    phoneGreetingCustom: {
      type: String,
      default: ""
    },
    refs: {
      type: Object,
      default: () => {}
    },
    socialSignIn: {
      type: Object,
      default: () => {}
    },
    subTitle: {
      type: String,
      default: ""
    },
    type: {
      required: true,
      type: String
    },
    campaignDesignColors: {
      type: Object,
      default: () => ({})
    },
    advocateCampaignShareUrl: {
      type: Boolean,
      default: false
    },
    zipAddressMapping: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      cardElement: "",
      context: "forms",
      inputFieldValues: {},
      handleInputDebouncers: {},
      selectedAddress: {
        address1: null,
        zip5: null,
        country: null,
      }
    };
  },
  computed: {
    ...mapGetters({
      activeStep: "navigation/activeStep",
      activeStepNumber: "navigation/activeStepNumber",
      activeStepTotal: "navigation/totalStepNumber",
      cookieBadgeVisible: "forms/cookieBadgeVisible"
    }),
    ...mapState("campaign", {
      splitAdvocateNames: "splitAdvocateNames"
    }),
    canAutoSkipStep() {
      // allow skipped custom fields to be auto-skipped after first action
      const isSkippableCustomField =
        this.legislatorsPulled &&
        this.isCustomStep &&
        this.step.custom.skippable;
      
      return (
        this.isMultistep &&
        !this.needAdditionalFields &&
        (!this.isFormSkippable || isSkippableCustomField)
      );
    },
    debouncedInput() {
      const self = this;
      return _debounce((field, value) => {
        self.updateInputFieldValues(field, value);
        self.handleInput(field, value);
      }, 400);
    },
    fetchLegislatorsPayload() {
      return this.$store.getters["legislators/fetchLegislatorsAddressPayload"];
    },
    hasMissingFormFields() {
      // filter out fields that are not required
      const requiredActiveFormFields = _keys(this.activeFormFields).filter(
        field => {
          if (field === "custom") {
            const activeField = this.activeFormFields[field];
            const isSkippable = activeField.skippable || false;
            const isMissingValue = _isEmpty(
              this.formValues[[activeField.name]]
            );
            
            return !isSkippable && isMissingValue;
          }
          
          return !_isEmpty(this.requiredActiveFields[field]);
        }
      );
      
      return (
        !_isEmpty(requiredActiveFormFields) && !_isEmpty(this.frozenFormValues)
      );
    },
    isCustomStep() {
      if (!this.currentStep) {
        return false;
      }
      return this.currentStep.startsWith("custom_");
    },
    isPetitionCampaign() {
      return this.type === CampaignTypes.PETITION;
    },
    legislatorResultsCancelTokenSource() {
      return this.cancelSources.legislatorResults;
    },
    addressAutocomplete() {
      return this.$store.getters[`${this.context}/addressAutocomplete`];
    },
    customFieldsContext() {
      return `${this.context}/customFields`;
    },
    customFields() {
      return this.$store.getters[this.customFieldsContext];
    },
    fieldGroupTag() {
      return this.advancedAccessibility ? "div" : "fieldset";
    },
    fieldLegendTag() {
      return this.advancedAccessibility ? "h2" : "legend";
    },
    frozenFormValues() {
      return this.$store.getters[`${this.context}/frozenFormValues`];
    },
    getZipFieldMaxLength() {
      if (this.isCountryFieldValueUS) {
        return "5";
      }
      return "";
    },
    greeting() {
      const date = new Date();
      const hour = date.getHours();
      let greeting = this.greetingEvening;
      
      if (hour >= 3 && hour < 12) {
        greeting = this.greetingMorning;
      } else if (hour >= 12 && hour < 17) {
        greeting = this.greetingAfternoon;
      }
      
      return greeting;
    },
    getZipFieldTypeAttribute() {
      const ZIP5_FIELD_INPUT_TYPE_NUMBER = "number";
      const ZIP5_FIELD_INPUT_TYPE_TEXT = "text";
      
      return this.isCountryFieldValueUS
        ? ZIP5_FIELD_INPUT_TYPE_NUMBER
        : ZIP5_FIELD_INPUT_TYPE_TEXT;
    },
    getZipFieldPatternAttribute() {
      const ZIP5_FIELD_INPUT_PATTERN_NUMBER = "[0-9]*";
      const ZIP5_FIELD_INPUT_PATTERN_TEXT = "";
      
      return this.isCountryFieldValueUS
        ? ZIP5_FIELD_INPUT_PATTERN_NUMBER
        : ZIP5_FIELD_INPUT_PATTERN_TEXT;
    },
    hasAdvocateBadge() {
      return this.advocateInfo.id !== undefined;
    },
    hasAdvocateData() {
      return !_isEmpty(this.advocateInfo);
    },
    hasAdvocatePhone() {
      if (this.advocateCampaignShareUrl) {
        return false;
      }
      
      return (
        !_isEmpty(this.advocateInfo.phones) &&
        !_isEmpty(this.advocateInfo.phones[0].address)
      );
    },
    advocatePhone() {
      return this.hasAdvocatePhone ? this.advocateInfo.phones[0].address : "";
    },
    advocateCountry() {
      return !_isEmpty(this.advocateInfo.country)
        ? this.advocateInfo.country
        : null;
    },
    countryValue() {
      const { country } = this.formValues;
      return !_isEmpty(country) ? country : this.countryDefault;
    },
    hasCountryField() {
      let hasCountryField = false;
      
      _forEach(this.formFields, step => {
        _forEach(step, fields => {
          if (fields.country && fields.country.isOn) {
            hasCountryField = true;
          }
        });
      });
      
      return hasCountryField;
    },
    isCountryFieldValueUS() {
      const FORM_VALUES_COUNTRY_US = "US";
      return this.countryValue === FORM_VALUES_COUNTRY_US;
    },
    validationErrors() {
      return (
        this.$store.getters[this.validationResultsContext][
          this.currentAction
          ] || {}
      );
    },
    validationResultsContext() {
      return `${this.context}/validationResults`;
    },
    valuesContext() {
      return `${this.context}/formValues`;
    },
    formValues() {
      return this.$store.getters[this.valuesContext];
    },
    stepProgressStyle() {
      if (this.currentStep === "welcome") {
        return { display: "none" };
      } else if (this.activeStepTotal < 2) {
        return { visibility: "hidden", height: "1.5rem" };
      }
      
      return {};
    },
    step() {
      return this.actionSteps[this.currentStep];
    },
    stepGreeting() {
      const greetingProp = `${this.currentStep}StepGreeting`;
      let greeting = "";
      
      if (typeof this[greetingProp] !== "undefined") {
        greeting = this[greetingProp];
      }
      
      return this.populateMergeVariables(greeting, this.mergeVariables);
    },
    welcomeStepGreeting() {
      return !_isEmpty(this.cardDescriptions.name)
        ? this.cardDescriptions.name
        : `${this.greeting} ${this.nameRequestLabel}`;
    },
    locationStepGreeting() {
      const nameGreeting = this.mergeVariables.advFirst
        ? "Great to meet you, [@advFirst]! "
        : "";
      
      return !_isEmpty(this.cardDescriptions.address)
        ? this.cardDescriptions.address
        : `${nameGreeting}<strong>Where do you live</strong>? This will help\n` +
        "me connect you with the right legislators.";
    },
    phoneStepGreeting() {
      if (!_isEmpty(this.cardDescriptions.phone)) {
        return this.cardDescriptions.phone;
      }
      
      return _isEmpty(this.customFields)
        ? this.phoneGreeting
        : this.phoneGreetingCustom;
    },
    emailStepGreeting() {
      return !_isEmpty(this.cardDescriptions.email)
        ? this.cardDescriptions.email
        : (this.emailQuestion || "The first way we are going to take action is by sending an email\n" +
        "to your legislators. So first, <strong>what is your email</strong>?");
    },
    actionSteps() {
      return this.formFields[this.currentAction];
    },
    activeActionSteps() {
      return _pickBy(
        this.actionSteps,
        step => !_isEmpty(this.filterStepFieldsNotFrozen(step))
      );
    },
    actionFieldsByName() {
      const flatFields = {};
      _forEach(this.actionSteps, step => {
        _forEach(step, stepField => {
          flatFields[stepField.name] = stepField;
        });
      });
      return flatFields;
    },
    activeFormFields() {
      return this.filterStepFieldsNotFrozen(this.step);
    },
    needAdditionalFields() {
      return this.$store.getters[`${this.context}/needAdditionalFields`][
        this.currentAction
        ];
    },
    showFacebookPrefill() {
      const facebookPrefillEnabled = this.socialSignIn.facebookEn || false;
      
      return this.isPetitionCampaign && facebookPrefillEnabled;
    },
    showForm() {
      return this.showStandardFieldsForm || this.showCustomFieldsForm;
    },
    showStandardFieldsForm() {
      return (
        this.needAdditionalFields ||
        (!this.hasAdvocateBadge && this.formDisplayAllowed)
      );
    },
    showCustomFieldsForm() {
      if (_isEmpty(this.customFields)) {
        return false;
      }
      
      return (
        this.showStandardFieldsForm ||
        (this.hasAdvocateBadge && this.formDisplayAllowed)
      );
    },
    formDisplayAllowed() {
      return !this.legislatorsPulled && !this.completedAllActions;
    },
    requiredActiveFields() {
      const activeFields = this.activeFormFields;
      return _pickBy(
        activeFields,
        field =>
          !_isEmpty(field.validationRules)
            ? field.validationRules.required
            : false
      );
    },
    titleDefaultSelected() {
      return this.$store.getters[`${this.context}/titleDefaultSelected`];
    },
    // Calculate number of progress bar segments, ignoring "Welcome" step
    visibleProgressSteps() {
      const visibleSegments = _filter(
        this.actionSteps,
        (value, key) => key !== "welcome"
      );
      return visibleSegments.length;
    }
  },
  watch: {
    currentAction: "onCreated",
    currentStep: "initStep",
    legislatorsPulled: "slideCardDown",
    watchActionAndLegislators: "checkMissingFields",
    countryValue: "validateZip5Field",
    cookieBadgeVisible(badgeVisible) {
      if (badgeVisible) {
        const completedStepCount = Object.keys(this.activeActionSteps).filter(
          step => !step.includes("custom")
        ).length;
        this.setActiveStepNumber(completedStepCount);
      }
    }
  },
  created() {
    this.onCreated();
  },
  inject: {
    $validator: { from: "$validator" },
    eventsBus: { from: "eventsBus" },
    formHelper: { from: "formHelper" }
  },
  methods: {
    ...mapActions({
      setActiveStepNumber: "navigation/activeStepNumber"
    }),
    filterStepFieldsNotFrozen(step) {
      return _pickBy(step, (field, fieldName) => {
        const fieldIsActivePhone =
          fieldName === "phone" && this.hasAdvocatePhone;
        return (
          field.isOn === true &&
          this.frozenFormValues[fieldName] === undefined &&
          !fieldIsActivePhone
        );
      });
    },
    debounceInput(fieldName, $event) {
      const field = this.actionFieldsByName[fieldName];
      
      if (field) {
        const debouncer = this.handleInputDebouncers[fieldName];
        
        if (typeof debouncer === "function") {
          const fieldValue = $event.target.value;
          this.updateInputFieldValues(field, fieldValue);
          
          debouncer(field, fieldValue);
        }
      }
    },
    debounceSelect(fieldName, $event) {
      const field = this.actionFieldsByName[fieldName];
      
      if (field) {
        this.updateInputFieldValues(field, $event.target.value);
        this.handleSelect(field, $event.target.value);
      }
    },
    /*
      Calculate the header height bottom margin and the call to action height so we know how much to slide the card up
     */
    getSlideHeight(headerEl, ctaEl) {
      const headerMarginBottom = window.getComputedStyle(headerEl).marginBottom; // Returns a string (ie. 10px)
      const ctaHeight = ctaEl.offsetHeight;
      const ctaMarginBottom = window.getComputedStyle(ctaEl).marginBottom;
      
      return (
        parseInt(headerMarginBottom, 10) +
        ctaHeight +
        parseInt(ctaMarginBottom, 10)
      );
    },
    async initStep() {
      if (!this.currentStep) {
        return;
      }
      
      const self = this;
      
      self.initFormValidation();
      
      self.initAdvocatePhoneValue();
      self.initCountry();
      self.initEventBus();
      
      await self.initFieldValues();
      
      if (this.canAutoSkipStep) {
        if (this.currentStep === 'location' && !this.googleAutocompleteIsInit()) {
          setTimeout(self.initAddressAutocomplete, 10);
        }
        
        this.eventsBus.$emit("skipStep", true);
        return;
      }
      
      self.addAutoCompleteListeners();
      if (
        !this.isMultistep &&
        _isEmpty(this.activeFormFields.zip5) &&
        _isEmpty(self.legislatorResultsCancelTokenSource)
      ) {
        self.handlePreFetchLegislators();
      }
      
      // TODO: figure out how to remove this
      if (this.isMultistep && !this.googleAutocompleteIsInit()) {
        setTimeout(self.initAddressAutocomplete, 10);
      }
    },
    googleAutocompleteIsInit() {
      return this.addressAutocomplete && this.addressAutocomplete.types;
    },
    initCountry() {
      // TODO: revisit what happens when self.step.country is null or undefined
      // currently it throws an eror in the template
      if (this.step.country && this.step.country.name) {
        const selectedCountry = this.advocateCountry || this.countryValue;
        this.handleSelect(this.step.country, selectedCountry, false);
      }
    },
    initEventBus() {
      this.eventsBus.$on("attemptedTakeAction", this.setFrozenFormValues);
      this.eventsBus.$on("checkFormValidation", this.handleFormValidation);
      this.eventsBus.$on("actionActivated", this.onActionActivated);
    },
    async initFieldValues() {
      this.$store.dispatch(this.customFieldsContext, this.getCustomFields());
      
      await this.populateValuesFromAdvocate();
      this.populateCustomFieldsValues();
      this.checkMissingFields();
    },
    updateInputFieldValues(fieldObj, fieldValue) {
      const { name } = fieldObj;
      this.inputFieldValues[name] = fieldValue;
      if (name === 'zip5') {
        this.formValues.state = this.zipAddressMapping[fieldValue]
          ? this.zipAddressMapping[fieldValue].administrative_area_level_1
          : '';
        this.formValues.city = this.zipAddressMapping[fieldValue]
          ? (this.zipAddressMapping[fieldValue].locality || this.zipAddressMapping[fieldValue].sublocality_level_1 || '')
          : '';
      }
    },
    onCreated() {
      const defaultStep = _keys(this.actionSteps)[0];
      
      if (this.currentStep !== defaultStep) {
        const totalSteps = _keys(this.activeActionSteps).length;
        
        this.$store.dispatch("navigation/activeStep", defaultStep);
        this.$store.dispatch("navigation/activeStepNumber", 1);
        this.$store.dispatch("navigation/totalStepNumber", totalSteps);
      } else {
        this.initStep();
      }
      
      this.initHandleInputDebouncers();
    },
    initHandleInputDebouncers() {
      const self = this;
      
      _forEach(self.actionSteps, step => {
        _forEach(step, stepField => {
          self.handleInputDebouncers[stepField.name] = _debounce(
            (field, value) => {
              self.handleInput(field, value);
            },
            800
          );
        });
      });
    },
    addAutoCompleteListeners() {
      // listen for fields to be auto-filled
      // (needed for Firefox/Edge since the blur event is not fired when field is auto-filled)
      
      // https://stackoverflow.com/questions/9847580/how-to-detect-safari-chrome-ie-firefox-and-opera-browser
      if (
        !this.isMultistep &&
        (typeof InstallTrigger !== "undefined" ||
          navigator.userAgent.indexOf("Firefox") > -1 ||
          navigator.userAgent.indexOf("Edge") > -1)
      ) {
        ["name", "address1", "country", "zip5", "phone", "email"].forEach(
          field => {
            const fieldId = `input-${field}`;
            const input = document.getElementById(fieldId);
            
            if (input) {
              if (fieldId === "input-address1") {
                setTimeout(() => {
                  input.setAttribute("autocomplete", "street-address");
                }, 1000);
              }
              this.registerOnAutoComplete(`#${fieldId}`).then(element =>
                this.handleInput(
                  this.step[field === "name" ? "fullName" : field],
                  element.value
                )
              );
            }
          }
        );
      }
    },
    checkMissingFields() {
      if (this.hasAdvocateBadge) {
        this.setFrozenFormValues();
      }
      
      const hasUnfilledFormFields = this.hasMissingFormFields;
      
      if (this.shouldUpdateAdvocate(hasUnfilledFormFields)) {
        this.eventsBus.$emit("createOrUpdateAdvocate", {
          formValues: this.formValues
        });
      }
      
      this.$store.dispatch(`${this.context}/needAdditionalFields`, {
        action: this.currentAction,
        needAdditionalFields: hasUnfilledFormFields
      });
    },
    shouldUpdateAdvocate(missingFieldsBool) {
      // We need to update advocate record if the legislators have already been pulled
      // and advocate just finished providing missing fields
      return (
        this.legislatorsPulled &&
        this.needAdditionalFields !== missingFieldsBool &&
        missingFieldsBool === false
      );
    },
    initAddressAutocomplete() {
      const autocompleteSettings = {
        fields: new AutocompleteFieldsOption(),
        types: ["address"],
        componentRestrictions: { country: this.countryValue }
      };
      // initialize Google address autocomplete on Address Street 1 field
      if (!_isEmpty(this.activeFormFields.address1) && this.$refs.address1) {
        let field = this.$refs.address1;
        
        if (field.$refs) {
          [field] = field.$refs.addressWrapper.children;
        }
        
        // eslint-disable-next-line no-undef
        const googleAddress = new google.maps.places.Autocomplete(
          field,
          autocompleteSettings
        );
        this.$store.dispatch(
          `${this.context}/addressAutocomplete`,
          googleAddress
        );
        this.addressAutocomplete.addListener(
          "place_changed",
          this.addressAutocompleteSelected
        );
      }
    },
    addressAutocompleteSelected() {
      const place = this.addressAutocomplete.getPlace();
      const unfilteredAddressComponents = place
        .address_components;
      
      const addressComponents = this.extractGoogleAutocompleteAddress(
        unfilteredAddressComponents
      );
      
      const streetNumber = addressComponents.street_number || "";
      const route = addressComponents.route || "";
      const address1 = `${streetNumber} ${route}`.trim();
      const zip5 =
        addressComponents.postal_code ||
        addressComponents.postal_code_prefix ||
        "";
      const zip4 = addressComponents.postal_code_suffix || "";
      const country = addressComponents.country || "";
      const state = addressComponents.administrative_area_level_1 || '';
      const city = addressComponents.locality || addressComponents.sublocality_level_1 || '';
      
      let county = addressComponents.administrative_area_level_2;
      county = county ? county.replace(' County', '') : '';
      
      if (zip5) {
        this.zipAddressMapping[zip5] = addressComponents;
      }
      
      // When the country is on for a campaign, there is always a default
      // value set for it. If no setting for country, we default to US
      // Thus, validation for the country will always pass for all other
      // address fields, which is why country must be checked first so
      // send the correct value for the country.
      if (this.activeFormFields.country && country) {
        this.handleInput(this.activeFormFields.country, country);
        
        if (!this.$refs.country._isVue) {
          this.$refs.country.value = country;
        }
      }
      
      if (this.activeFormFields.address1 && address1) {
        this.handleInput(this.activeFormFields.address1, address1);
        
        if (!this.$refs.address1._isVue) {
          this.$refs.address1.value = address1;
        }
        
        this.formValues.state = state;
        this.formValues.city = city;
      }
      
      if (this.activeFormFields.zip5 && zip5) {
        this.handleInput(this.activeFormFields.zip5, zip5);
        
        if (!this.$refs.zip5._isVue) {
          this.$refs.zip5.value = zip5;
        }
        
        this.formValues.state = state;
        this.formValues.city = city;
      }
      
      if (zip4) {
        this.formValues.zip4 = zip4;
      }
      
      if (county) {
        this.formValues.county = county;
      }
      
      if (place.geometry) {
        this.formValues.lat = place.geometry.location.lat();
        this.formValues.lon = place.geometry.location.lng();
        this.selectedAddress.address1 = address1;
        this.selectedAddress.zip5 = zip5;
        this.selectedAddress.country = country;
      }
    },
    getFieldValue(field) {
      if (field === undefined) {
        return field;
      }
      return (
        this.formValues[field.name] ||
        this.inputFieldValues[field.name] ||
        field.value
      );
    },
    // TODO - Incorrectly setting front end values. See PDEV-6361.
    getCheckBoxFieldValue(field) {
      let fieldValue = this.getFieldValue(field);
      
      if (!field.options || !field.options.length) {
        return fieldValue;
      }
      
      if (typeof fieldValue === "string") {
        try {
          // turn multiple values into an array
          fieldValue = JSON.parse(fieldValue);
        } catch (e) {
          // turn single strings into an array
          fieldValue = [fieldValue];
        }
      }
      
      return fieldValue || [];
    },
    getSelectFieldValue(field) {
      const fieldValue = this.getFieldValue(field);
      return _isEmpty(fieldValue) ? "" : fieldValue;
    },
    getFieldType(field) {
      const inputType = (field.inputType || '').toLowerCase();
      // TODO:: Put input types in a constants js (constants/formsfields.js) file and pull them from there
      const nonInputTypes = ["radio", "select", "textarea", "checkbox"];
      if (nonInputTypes.indexOf(inputType) !== -1) {
        return inputType;
      }
      return "input";
    },
    findAndParseStringByCountry(str) {
      const PARSE_COUNTRY_KEY_US = "US:";
      const PARSE_COUNTRY_KEY_OTHER = "Other:";
      
      if (str === undefined) {
        return null;
      }
      
      const strOptions = str.split("|");
      const strMatcher = this.isCountryFieldValueUS
        ? PARSE_COUNTRY_KEY_US
        : PARSE_COUNTRY_KEY_OTHER;
      const strElement = strOptions.find(el => el.startsWith(strMatcher));
      
      if (!strElement) {
        return null;
      }
      
      return strElement.substring(strMatcher.length);
    },
    transformCustomFieldOptionsForSelect(field) {
      const options = this.getFieldProp(field, "options", []);
      
      return options.map(option => ({ label: option, value: option }));
    },
    getFieldProp(field, prop, defaultValue = "") {
      return field && field[prop] !== undefined ? field[prop] : defaultValue;
    },
    getFieldPlaceholder(field, defaultValue = "", isRequired = false) {
      const fieldPlaceholder = this.getFieldProp(
        field,
        "placeholder",
        defaultValue
      );
      const APPEND_ASTERISK = " *";
      if (!(isRequired && this.advancedAccessibility)) {
        // If the field is not required or the user does not have Advanced Accessibility enabled
        // We don't need to include the asterisk in the placeholder, so just return the raw placeholder
        return fieldPlaceholder;
      } else if (fieldPlaceholder.includes("|")) {
        // If there are different placeholders depending on the country (like ZIP/Postal Code)
        // The placeholders are delimited by a `|`
        // So, we add the asterisk after each placeholder
        return fieldPlaceholder
          .split("|")
          .map(placeholder => placeholder + APPEND_ASTERISK)
          .join("|");
      }
      // Otherwise, there is only one placeholder, so we just add it
      return (
        this.getFieldProp(field, "placeholder", defaultValue) + APPEND_ASTERISK
      );
    },
    getFieldName(field) {
      return this.getFieldProp(field, "name");
    },
    getFieldDescription(field) {
      return this.getFieldProp(field, "description");
    },
    // placeholders should be null by default, unless used with a label
    getZipFieldPlaceholder(placeholderRaw, defaultValue = null) {
      if (_isEmpty(placeholderRaw)) {
        return defaultValue;
      }
      
      return this.findAndParseStringByCountry(placeholderRaw);
    },
    getZipFieldValidationError(validationErrorRaw) {
      // separates values list from the rest of the validation error
      const VALUES_LIST_DEMARCATION = "!!";
      if (_isEmpty(validationErrorRaw)) {
        return null;
      }
      
      const validationError = validationErrorRaw.split(
        VALUES_LIST_DEMARCATION
      )[0];
      const validationErrorValues = validationErrorRaw.split(
        VALUES_LIST_DEMARCATION
      )[1];
      const errorByCountry = this.findAndParseStringByCountry(
        validationErrorValues
      );
      
      return validationError.concat(errorByCountry);
    },
    checkFieldValid(field) {
      const requiredFields = Object.keys(this.requiredActiveFields);
      const currentActionFieldSetting = this.step[field];
      const validationErrors = this.validationErrors[field];
      // Only do address validation if field is required
      
      const fieldEnabled =
        currentActionFieldSetting && currentActionFieldSetting.isOn;
      const fieldRequired = requiredFields.includes(field);
      const validationFailed =
        !_isEmpty(validationErrors) || validationErrors !== null;
      const fieldNotPopulated = _isEmpty(this.formValues[field]);
      
      return !(
        fieldEnabled &&
        fieldRequired &&
        (validationFailed || fieldNotPopulated)
      );
    },
    validateAllRequiredAddressFields() {
      // TODO: This is weird. Why aren't we just checking each address field is valid??
      const addressFields = ["address1", "city", "state", "country", "zip5"];
      
      return addressFields.every(addressField => {
        const otherAddressFields = addressFields.filter(
          addressComponent => addressComponent !== addressField
        );
        
        return otherAddressFields.every(this.checkFieldValid);
      });
    },
    handleFormValidation() {
      _forOwn(this.activeFormFields, field => {
        this.handleFormFieldValue(
          field.inputType,
          field,
          this.getFieldValue(field),
          false
        );
      });
    },
    async handleFormFieldValue(
      fieldCategory,
      field,
      value,
      fetch = true,
      emitEvent = true,
      isManual = false
    ) {
      try {
        const validationResults = await this.validatorHelper.isFieldValid(
          field.name,
          value
        );
        const validationErrors = !validationResults.isValid
          ? validationResults.message
          : null;
        
        this.$store.dispatch(this.validationResultsContext, {
          action: this.currentAction,
          fieldName: field.name,
          value: validationErrors
        });
        
        if (value !== null && value !== undefined) {
          this.$store.dispatch(this.valuesContext, {
            fieldName: field.name,
            value
          });
        }
        
        if (isManual) {
          this.detectedManualChange(field.name, value);
        }
        
        if (field.name === "country") {
          this.initAddressAutocomplete();
        }
        
        if (field.name === "title" && value === "") {
          this.$store.dispatch(`${this.context}/titleDefaultSelected`, true);
        } else {
          this.$store.dispatch(`${this.context}/titleDefaultSelected`, false);
        }
        
        // We want to pre-search legislators if we get an address and/or zip
        if (fetch && this.shouldPrefetchLegislators(field, validationResults)) {
          this.handlePreFetchLegislators();
        }
        
        this.eventsBus.$emit(fieldCategory, { field, value });
      } catch (error) {
        this.$store.dispatch(this.valuesContext, {
          fieldName: field.name,
          value
        });
        
        this.eventsBus.$emit(fieldCategory, { field, value });
      }
      
      if (emitEvent === true) {
        this.eventsBus.$emit("handleFormFieldValue", {
          action: this.currentAction,
          fieldCategory,
          field,
          value
        });
      }
    },
    detectedManualChange(fieldName, fieldValue) {
      const keys = ['zip5', 'address1', 'country'];
      const isInputAddressField = keys.includes(fieldName);
      const isGeocodedAddress = this.formValues.lat !== null && this.formValues.lon !== null;
      const isAddressChanged = this.selectedAddress[fieldName] !== null && this.selectedAddress[fieldName] !== fieldValue;
      
      if (isInputAddressField && isGeocodedAddress && isAddressChanged) {
        this.purgeGeocodedAddressData(fieldName === 'country');
      }
      
      if (fieldName === 'country') {
        this.handleInput(this.activeFormFields.address1, '');
        document.querySelectorAll(".pac-container").forEach(el => el.remove());
      }
    },
    purgeGeocodedAddressData(withFields = false) {
      this.formValues.lat = null;
      this.formValues.lon = null;
      this.formValues.state = null;
      this.formValues.city = null;
      this.formValues.county = null;
      this.formValues.zip4 = null;
      
      if (withFields) {
        this.purgeFormFieldsData(['address1', 'zip5']);
      }
    },
    purgeFormFieldsData(keys) {
      keys.forEach(key => {
        this.setFormFieldData(key, null);
      });
    },
    setFormFieldData(name, value) {
      this.formValues[name] = value;
      this.handleInput(this.activeFormFields[name], value);
      
      if (!this.$refs[name]._isVue) {
        this.$refs[name].value = value;
      }
    },
    shouldPrefetchLegislators(field, validationResults) {
      if (this.previewMode) {
        return false;
      }
      const requiredAddressFields = ["country", "address1", "zip5"];
      
      const { address1, zip5, country } = this.formValues;
      const lastPayload = this.fetchLegislatorsPayload;
      let hasAddressChanged = false;
      if (
        lastPayload.address1 !== address1 ||
        lastPayload.zip5 !== zip5 ||
        lastPayload.country !== country
      ) {
        hasAddressChanged = true;
      }
      
      return (
        requiredAddressFields.includes(field.name) &&
        validationResults.isValid &&
        hasAddressChanged &&
        this.validateAllRequiredAddressFields()
      );
    },
    handlePreFetchLegislators() {
      if (this.currentAction !== "default") {
        if (!this.formValues.country) {
          this.formValues.country = this.countryValue;
        }
        
        const advocateAddress = this.getAdvocateAddress(this.formValues);
        
        this.eventsBus.$emit("findLegislators", {
          background: true,
          address: advocateAddress
        });
      }
    },
    isCheckbox(field) {
      return this.getFieldType(field) === "checkbox";
    },
    isInput(field) {
      return this.getFieldType(field) === "input";
    },
    isRadio(field) {
      return this.getFieldType(field) === "radio";
    },
    isSelect(field) {
      return this.getFieldType(field) === "select";
    },
    isTextArea(field) {
      return this.getFieldType(field) === "textarea";
    },
    isMultipleOptionsCheckbox(field) {
      return this.isCheckbox(field) && !_isEmpty(field.options);
    },
    isSingleOptionCheckbox(field) {
      return this.isCheckbox(field) && _isEmpty(field.options);
    },
    isCheckBoxChecked(field, checkboxValue) {
      const currentCheckboxValue = this.getCheckBoxFieldValue(field);
      if (!field.options || !field.options.length) {
        return currentCheckboxValue === checkboxValue;
      }
      return currentCheckboxValue.indexOf(checkboxValue) !== -1;
    },
    isFieldRequired(field) {
      const type = this.getFieldProp(field, "type");
      const name = this.getFieldProp(field, "name");
      const skippable = this.getFieldProp(field, "skippable", false);
      const required = Object.keys(this.requiredActiveFields).includes(
        type === "custom" ? "custom" : name
      );
      
      return required && !skippable;
    },
    async handleInput(field, value, isManual = false) {
      if(field) {
        await this.handleFormFieldValue("input", field, value, true, true, isManual);
      }
    },
    handleCheckBox(field, value, isChecked) {
      if (!field.options || !field.options.length) {
        this.handleFormFieldValue("input", field, value);
        return;
      }
      
      const fieldValues = this.getCheckBoxFieldValue(field);
      const valueIndex = fieldValues.indexOf(value);
      if (!isChecked && valueIndex !== -1) {
        fieldValues.splice(valueIndex, 1);
      } else if (valueIndex === -1) {
        fieldValues.push(value);
      }
      this.handleFormFieldValue("input", field, fieldValues);
    },
    handleSelect(field, value, emitEvent = true, isManual = false) {
      this.handleFormFieldValue("select", field, value, true, emitEvent, isManual);
    },
    onActionActivated() {
      this.initFormValidation();
      this.$nextTick(() => {
        this.addAutoCompleteListeners();
      });
    },
    // populate store with values passed in from advocate cookie
    async populateValuesFromAdvocate() {
      if (!this.hasAdvocateData) {
        return;
      }

      // First, populate city and state directly from advocate data if available
      // These are often not form fields but are needed for address completion
      this.populateAddressFieldsFromAdvocate();

      // use asynchronous iterators to ensure we await validation results
      for await (let step of Object.values(this.actionSteps)) {
        for await (let field of Object.values(step)) {
          const { name } = field;
          const fieldNotPopulated = _isEmpty(this.formValues[name]);
          
          if (name !== undefined) {
            let value = this.advocateInfo[name.toLowerCase()];
            
            if (name === "title") {
              // we call the title field a 'prefix' in our platform
              // we used prefix in the advocate cookie if it's created from a Classic / Minimo campaign
              // this should be removed once we do away with Classic/Minimo
              value = this.advocateInfo.title || this.advocateInfo.prefix;
            } else if (name === "phone") {
              // we return phone numbers in a format that the validator isn't expecting (+11234567890)
              value = this.interpretPhoneNumber(this.advocateInfo.phone);
            }
            
            // check to see if input values are in list of options for select fields
            if (name === "title" || name === "country") {
              if (
                field.options.filter(option => option.value === value)
                  .length === 0
              ) {
                value = "";
              }
            }
            
            if (!_isEmpty(value) && fieldNotPopulated) {
              await this.handleInput(field, value);

              // If we just populated a zip5 field, also update city/state from zipAddressMapping if available
              if (name === 'zip5' && this.zipAddressMapping[value]) {
                const addressData = this.zipAddressMapping[value];
                if (addressData.administrative_area_level_1 && _isEmpty(this.formValues.state)) {
                  this.formValues.state = addressData.administrative_area_level_1;
                }
                if (addressData.locality && _isEmpty(this.formValues.city)) {
                  this.formValues.city = addressData.locality;
                }
              }
            }
          }
        }
      }
    },
    populateAddressFieldsFromAdvocate() {
      // Populate city and state directly from advocate data if they exist
      // These fields are often not part of the form fields but are needed for address completion
      const addressFields = ['city', 'state', 'county', 'zip4', 'lat', 'lon'];

      addressFields.forEach(fieldName => {
        const advocateValue = this.advocateInfo[fieldName];
        const currentValue = this.formValues[fieldName];

        // Only populate if advocate has the value and form doesn't already have it
        // Check for both empty string and undefined/null values
        if (!_isEmpty(advocateValue) && (currentValue === '' || currentValue === null || currentValue === undefined)) {
          this.$store.dispatch(this.valuesContext, {
            fieldName: fieldName,
            value: advocateValue
          });
        }
      });
    },
    getCustomFields() {
      let fields = this.activeFormFields;
      
      if (this.isMultistep) {
        fields = this.actionFieldsByName;
      }
      
      return _pickBy(fields, field => field.type === "custom");
    },
    populateCustomFieldsValues() {
      if (!this.hasAdvocateBadge) {
        return;
      }
      
      const customFields = this.getCustomFields();
      _forOwn(this.customFieldsValues, fieldRecord => {
        const fieldName = fieldRecord.prompt;
        const fieldValue = fieldRecord.value;
        const hasNonEmptyValue = fieldValue || !_isEmpty(fieldValue);
        // We only fill non-empty custom fields values.
        // If not, we would trigger validation if the field value is empty
        if (hasNonEmptyValue) {
          const formField = customFields[fieldName];
          this.handleInput(formField, fieldValue);
        }
      });
    },
    initAdvocatePhoneValue() {
      if (this.hasAdvocatePhone) {
        this.handleFormFieldValue(
          "input",
          { name: "phone" },
          this.advocatePhone
        );
      }
    },
    initFormValidation() {
      this.validatorHelper = new VueValidatorHelper(this.activeFormFields);
      this.validatorHelper.setValidator(this.$validator);
      this.formHelper.addZipValidationRule();
    },
    registerOnAutoComplete(elementSelector) {
      return new Promise(resolve => {
        const element = document.querySelector(elementSelector);
        let hasKeyInteraction = false;
        
        element.addEventListener("input", e => {
          if (hasKeyInteraction === false) {
            resolve(e.target);
          }
        });
        
        element.addEventListener("keydown", () => {
          // If there is a keyboard interaction then we believe it is not autocomplete
          hasKeyInteraction = true;
        });
      });
    },
    setFrozenFormValues() {
      // don't freeze anything that's invalid, ie switching between forms
      const frozenValues = _pickBy(
        this.formValues,
        (fieldValue, fieldName) =>
          !this.validationErrors[fieldName] && !_isEmpty(fieldValue)
      );
      this.$store.dispatch(`${this.context}/frozenFormValues`, frozenValues);
    },
    slideCardUp() {
      const headerElement = this.refs.header.$el;
      const ctaElement = this.refs.header.$refs.cta;
      
      if (this.isMobile && ctaElement) {
        const slideHeight = this.getSlideHeight(headerElement, ctaElement);
        const slideOffset = this.logoImageSource ? 5 : 0; // Add 5px to the slide if we have a logo
        const slideTotal = slideHeight + slideOffset;
        
        this.cardElement = this.$parent.$el;
        TweenMax.to(this.cardElement, 0.4, { y: `-${slideTotal}px` });
      }
    },
    slideCardDown() {
      if (this.isMobile && this.cardElement) {
        TweenMax.to(this.cardElement, 0, { y: 0, clearProps: "y" });
      }
    },
    validateZip5Field() {
      if (this.activeFormFields.zip5) {
        this.handleInput(
          this.activeFormFields.zip5,
          this.getFieldValue(this.activeFormFields.zip5)
        );
      }
    }
  }
};
</script>
